import { CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { DocumentSelector } from '@/components/treatment/v3/request/package-edit/DocumentSelector';
import { MatterSignatureCaptureModal } from '@/components/treatment/v3/request/review-submit/MatterSignatureCaptureModal';
import { isMatterSignatureSourceBlob } from '@/components/treatment/v3/request/review-submit/matterSignatureUtils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { SubmissionAttemptSignature } from '@/services/api/agentflowService';
import { usePatchSubmissionAttemptMutation } from '@/services/api/agentflowService';
import { useCreateBlobMutation,useGetBlobsQuery } from '@/services/api/blobService';
import type { BlobCreateRequest } from '@/services/types/blob-types';

const STRATEGY_LABELS: Record<string, string> = {
  docx_with_existing_signature_asset: 'Existing signature asset',
  docx_with_extracted_signature_asset: 'Extracted signature asset',
  signed_authorization_pdf_merge: 'Signed authorization PDF merge',
  blocked_missing_usable_signature: 'Missing usable signature',
};

interface SignatureSectionProps {
  signature: SubmissionAttemptSignature | null;
  matterId: string;
  requestId: string;
}

export const SignatureSection = ({
  signature,
  matterId,
  requestId,
}: SignatureSectionProps) => {
  const [patchAttempt] = usePatchSubmissionAttemptMutation();
  const [createBlob] = useCreateBlobMutation();
  const { data: blobs } = useGetBlobsQuery(matterId, { skip: !matterId });

  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);
  const [selectedBlobIds, setSelectedBlobIds] = useState<string[]>([]);
  const [isCaptureOpen, setIsCaptureOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const selectedBlob = useMemo(() => {
    if (!blobs || selectedBlobIds.length === 0) return null;
    return blobs.find((b) => b.id === selectedBlobIds[0]) ?? null;
  }, [blobs, selectedBlobIds]);

  const handleOpenFilePicker = useCallback(() => {
    setSelectedBlobIds([]);
    setIsFilePickerOpen(true);
  }, []);

  const handleFilePickerConfirm = useCallback(() => {
    setIsFilePickerOpen(false);
    setIsCaptureOpen(true);
  }, []);

  const handleSaveFile = useCallback(
    async (file: File) => {
      setIsSaving(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name);
        formData.append('original_filename', file.name);
        formData.append('file_mime_type', file.type || 'application/octet-stream');
        const blob = await createBlob({
          matterId,
          blob: formData as unknown as BlobCreateRequest,
        }).unwrap();
        await patchAttempt({
          matterId,
          requestId,
          body: {
            action_type: 'set_signature_asset',
            payload: { blob_id: blob.id },
          },
        }).unwrap();
        toast.success('Signature uploaded');
      } catch {
        toast.error('Failed to upload signature');
      } finally {
        setIsSaving(false);
      }
    },
    [matterId, requestId, createBlob, patchAttempt],
  );

  if (!signature) return null;

  const strategyLabel =
    STRATEGY_LABELS[signature.strategy ?? ''] ?? signature.strategy;

  // Resolved state
  if (signature.resolved) {
    return (
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">
          Signature
        </div>
        <div className="flex items-start gap-2.5">
          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-zinc-800">
              {strategyLabel}
            </div>
            {signature.manual_resolution && (
              <div className="text-xs text-zinc-500">
                {signature.manual_resolution}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Blocked state
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 mb-2">
        Signature
      </div>

      <div className="rounded-lg border-l-[3px] border-l-amber-400 border border-zinc-200 px-4 py-3 space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
          <span className="text-sm font-medium text-zinc-800">
            {strategyLabel}
          </span>
        </div>
        {signature.agent_search_summary && (
          <p className="text-xs text-zinc-500 leading-relaxed">
            {signature.agent_search_summary}
          </p>
        )}
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={isSaving}
          onClick={handleOpenFilePicker}
        >
          <FileText className="h-3.5 w-3.5" />
          {isSaving ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Saving...
            </>
          ) : (
            'Select from Matter'
          )}
        </Button>
      </div>

      {/* File picker dialog */}
      <Dialog open={isFilePickerOpen} onOpenChange={setIsFilePickerOpen}>
        <DialogContent className="sm:max-w-5xl p-0 flex flex-col gap-0">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle>Select a signed document</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 border-t border-zinc-200 h-[60vh]">
            <div className="w-[380px] shrink-0 overflow-y-auto p-4">
              <DocumentSelector
                matterId={matterId}
                selectedBlobIds={selectedBlobIds}
                onSelectionChange={setSelectedBlobIds}
                onConfirm={handleFilePickerConfirm}
                filterBlobs={isMatterSignatureSourceBlob}
                multiSelect={false}
                showConfirmButton
                confirmButtonLabel="Crop Signature Image from Document"
              />
            </div>
            <div className="flex-1 min-w-0 border-l border-zinc-200 bg-zinc-50 flex flex-col">
              {selectedBlob ? (
                <>
                  <div className="px-4 py-3 border-b border-zinc-200 bg-white shrink-0">
                    <p className="text-sm font-medium text-zinc-800 truncate">
                      {selectedBlob.name || selectedBlob.original_filename}
                    </p>
                  </div>
                  <div className="flex-1 min-h-0">
                    <iframe
                      title="Document preview"
                      src={selectedBlob.download_url}
                      className="h-full w-full border-0"
                    />
                  </div>
                </>
              ) : (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-sm text-zinc-500">
                    Select a document to preview
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature capture modal */}
      <MatterSignatureCaptureModal
        isOpen={isCaptureOpen}
        onClose={() => setIsCaptureOpen(false)}
        blob={selectedBlob}
        onSaveFile={handleSaveFile}
      />
    </div>
  );
};
